import React from 'react';
import {
  AlternateEmailRounded,
  ForumRounded,
  LocationOnRounded,
  PhoneRounded
} from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PublicPageLayout from '../components/PublicPageLayout';
import { FONT_FAMILY } from '../common/sharedFunctions';

const accent = '#B7EE00';
const ink = '#111411';

const ContactCard = ({ href, icon, label, value }) => (
  <Box
    component={href ? 'a' : 'div'}
    href={href}
    sx={{
      alignItems: 'center',
      backgroundColor: '#F5F7F1',
      border: '1px solid #E3E8DE',
      borderRadius: '18px',
      color: 'inherit',
      display: 'flex',
      gap: 2,
      minWidth: 0,
      p: 2.25,
      textDecoration: 'none',
      transition: 'border-color 180ms ease, transform 180ms ease',
      '&:hover': href ? {
        borderColor: 'rgba(183,238,0,0.95)',
        transform: 'translateY(-3px)'
      } : undefined
    }}
  >
    <Box sx={{
      alignItems: 'center',
      backgroundColor: ink,
      borderRadius: '13px',
      color: accent,
      display: 'flex',
      height: 46,
      justifyContent: 'center',
      minWidth: 46
    }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{
        color: '#727A71',
        fontFamily: FONT_FAMILY,
        fontSize: '0.7rem',
        fontWeight: 850,
        letterSpacing: '0.08em',
        mb: 0.35,
        textTransform: 'uppercase'
      }}>
        {label}
      </Typography>
      <Typography sx={{
        color: ink,
        fontFamily: FONT_FAMILY,
        fontSize: '0.95rem',
        fontWeight: 800,
        overflowWrap: 'anywhere'
      }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const ContactUs = () => {
  const { t } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const socialItems = settings ? [
    { className: 'fab fa-facebook-f', href: settings.FacebookHandle, label: t('follow_facebook') },
    { className: 'fab fa-twitter', href: settings.TwitterHandle, label: t('follow_twitter') },
    { className: 'fab fa-instagram', href: settings.InstagramHandle, label: t('follow_instagram') }
  ].filter((item) => item.href) : [];

  return (
    <PublicPageLayout
      eyebrow={t('get_in_touch', { defaultValue: 'Get in touch' })}
      icon={<ForumRounded sx={{ fontSize: 23 }} />}
      subtitle={t('contact_page_subtitle', {
        defaultValue: 'Questions about Laiwani? Reach the team through the contact details below.'
      })}
      title={t('contact_us')}
    >
      <Box sx={{
        display: 'grid',
        gap: { xs: 2.5, md: 3.5 },
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '0.85fr 1.15fr' }
      }}>
        <Box sx={{
          background: 'linear-gradient(145deg, #0C120E 0%, #152033 100%)',
          borderRadius: '22px',
          color: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: 330,
          overflow: 'hidden',
          p: { xs: 2.75, sm: 3.5 },
          position: 'relative'
        }}>
          <Box sx={{
            backgroundColor: 'rgba(183,238,0,0.1)',
            borderRadius: '50%',
            height: 260,
            position: 'absolute',
            right: -130,
            top: -120,
            width: 260
          }} />
          <Box sx={{ position: 'relative' }}>
            <Typography sx={{
              color: accent,
              fontFamily: FONT_FAMILY,
              fontSize: '0.73rem',
              fontWeight: 900,
              letterSpacing: '0.12em',
              mb: 1.5,
              textTransform: 'uppercase'
            }}>
              {t('laiwani_support', { defaultValue: 'Laiwani support' })}
            </Typography>
            <Typography component="h2" sx={{
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '1.75rem', sm: '2.15rem' },
              fontWeight: 900,
              letterSpacing: '-0.04em',
              lineHeight: 1.06,
              mb: 1.5
            }}>
              {settings && settings.CompanyName
                ? settings.CompanyName
                : t('talk_to_laiwani', { defaultValue: 'Talk to Laiwani' })}
            </Typography>
            <Typography sx={{
              color: 'rgba(255,255,255,0.67)',
              fontFamily: FONT_FAMILY,
              fontSize: '0.92rem',
              lineHeight: 1.7
            }}>
              {t('contact_support_description', {
                defaultValue: 'Use the most convenient channel and our team will help with your question.'
              })}
            </Typography>
          </Box>

          {socialItems.length > 0 ? (
            <Box sx={{ position: 'relative' }}>
              <Typography sx={{
                color: 'rgba(255,255,255,0.55)',
                fontFamily: FONT_FAMILY,
                fontSize: '0.7rem',
                fontWeight: 850,
                letterSpacing: '0.1em',
                mb: 1.25,
                textTransform: 'uppercase'
              }}>
                {t('follow_us', { defaultValue: 'Follow us' })}
              </Typography>
              <Stack direction="row" spacing={1}>
                {socialItems.map((item) => (
                  <Box
                    aria-label={item.label}
                    component="a"
                    href={item.href}
                    key={item.href}
                    rel="noreferrer"
                    target="_blank"
                    sx={{
                      alignItems: 'center',
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: '12px',
                      color: '#FFFFFF',
                      display: 'flex',
                      height: 42,
                      justifyContent: 'center',
                      textDecoration: 'none',
                      transition: 'background-color 180ms ease, color 180ms ease',
                      width: 42,
                      '&:hover': { backgroundColor: accent, color: ink }
                    }}
                  >
                    <i className={item.className} />
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : null}
        </Box>

        <Stack spacing={1.5}>
          {settings && settings.CompanyAddress ? (
            <ContactCard
              icon={<LocationOnRounded />}
              label={t('address', { defaultValue: 'Address' })}
              value={settings.CompanyAddress}
            />
          ) : null}
          {settings && settings.contact_email ? (
            <ContactCard
              href={`mailto:${settings.contact_email}`}
              icon={<AlternateEmailRounded />}
              label={t('email')}
              value={settings.contact_email}
            />
          ) : null}
          {settings && settings.CompanyPhone ? (
            <ContactCard
              href={`tel:${settings.CompanyPhone}`}
              icon={<PhoneRounded />}
              label={t('phone')}
              value={settings.CompanyPhone}
            />
          ) : null}
          <Box sx={{
            backgroundColor: accent,
            borderRadius: '18px',
            color: ink,
            mt: 'auto !important',
            p: 2.5
          }}>
            <Typography sx={{
              fontFamily: FONT_FAMILY,
              fontSize: '1rem',
              fontWeight: 900,
              mb: 0.6
            }}>
              {t('contact_details_heading', { defaultValue: 'Clear details, direct support.' })}
            </Typography>
            <Typography sx={{
              color: 'rgba(17,20,17,0.68)',
              fontFamily: FONT_FAMILY,
              fontSize: '0.84rem',
              lineHeight: 1.6
            }}>
              {t('contact_details_note', {
                defaultValue: 'For account or ride-related questions, include the relevant trip details when possible.'
              })}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </PublicPageLayout>
  );
};

export default ContactUs;
